"use strict";
import { getProduct, getProductPacket, getCompanionProduct } from "../graphql/mob_queries";
import { getGroupCourseByMssId, listMOBConfigsByAppInstanceMobVersion, listMOBConfigsByAppInstanceDeploymentId } from "../graphql/queries";
import { rentSizeAvailability } from "../graphql/queries";
import { rentArticleAvailability } from "../graphql/queries";
import { listProductAvailabilityIntervalsByProduct } from "../graphql/queries";
import { getStaticContent } from "../graphql/queries";
import {
  listRentProductRequirementByAppInstance,
} from "../graphql/queries";
import { listRentCategoriesByProductType, listSchoolCategoriesByProductType } from "../graphql/mob_queries";
import { listLocationsByAppInstance } from "../graphql/mob_queries";
import { listPointSellsByAppInstance } from "../graphql/mob_queries";
import { listPointSellOpenHoursByScope } from "../graphql/mob_queries";
import { listPointMeetingsByAppInstance } from "../graphql/mob_queries";
import { listSkillLevelClientsByAppInstance } from "../graphql/queries";
import { createOrderrDraft } from "../graphql/mob_queries";
import { getOrderr } from "../graphql/mob_queries";
import { removeFromCart } from "../graphql/mob_queries";
import { addToCart } from "../graphql/mob_queries";
import { updateOrderrPickupAndPaymentInfo } from "../graphql/mob_queries";
import { checkoutOrderr } from "../graphql/mob_queries";
import { getPayment } from "../graphql/mob_queries";
import { listStaticContentByAppInstance } from "../graphql/mob_queries";
import { schoolListVariants } from "../graphql/queries";
import { schoolListFreeSlots } from "../graphql/queries";
import { schoolListVariantsV2 } from "../graphql/queries";
import { schoolListFreeSlotsV2 } from "../graphql/queries";
import { schoolListGroupCourse } from "../graphql/queries";
import { schoolListGroupCourseLesson } from "../graphql/queries";
import { applyDiscountCode, removeDiscountCode } from "../graphql/mob_queries";
import { sendCourseInquiry } from "../graphql/mutations";
import { API, graphqlOperation } from "aws-amplify";
import moment from "moment";
import { toWireMinute } from "../libs/WallClock.js";

class APIAdapter {
  static get AUTH_MODE_API_KEY() {
    return "API_KEY";
  }

  /**
   * The backend's language-code case convention, in one place (MOB-129).
   *
   * MSS reads the same ISO 639-1 code in two shapes: what is STORED on an order item or sent
   * in an inquiry is upper-case, what FILTERS an availability query is lower-case. The UI
   * works in lower-case codes throughout; the booking views used to upper-case them by hand
   * before dispatching to the cart, one copy of the rule per view. The adapter that talks to
   * the backend owns the conversion now.
   *
   * A null/undefined list is passed through unchanged — the mutation distinguishes "no
   * languages given" from an empty list.
   *
   * @param {Array<string>|null} languages
   * @returns {Array<string>|null}
   */
  static toStoredLanguageCodes(languages) {
    if (languages == null) {
      return languages;
    }
    return languages.map((code) => String(code).trim().toUpperCase());
  }

  /** The same convention, for the availability queries' `languageCodes` filter. */
  static toFilterLanguageCodes(languages) {
    if (languages == null) {
      return languages;
    }
    return languages.map((code) => String(code).trim().toLowerCase());
  }

  /**
   * The age of the course inquiry as the `Int` that `input CourseInquiryPayload` declares
   * (MOB-239 review). The text field of the form gives a string, and the GraphQL spec rejects
   * a string for an `Int`. The form rule `between:3,120` reads the value with `Number`, so this
   * does too, and gives whole years. An empty value gives `null`.
   *
   * @param {string|number|null} age
   * @returns {number|null}
   */
  static toInquiryAge(age) {
    if (age == null || String(age).trim() === "") {
      return null;
    }
    const years = Math.trunc(Number(age));
    return Number.isFinite(years) ? years : null;
  }

  static async graphQLCall(graphqlOperation, authMode) {
    let graphQLOptions = graphqlOperation;

    let graphQLOptionsApiKey = {
      ...graphQLOptions,
      authMode: authMode,
    };

    return API.graphql(graphQLOptionsApiKey);
  }

  /**
   * Nacte hlavni konfiguraci Mates On-line Bookingu
   * API_KEY
   *
   * @param {String} appInstance
   * @param {Number} deploymentId - Optional deployment ID to filter config by specific deployment
   * @returns
   */
  static async getLatestMOBConfig(appInstance, deploymentId = null) {
    let resultSet;

    // If deploymentId is provided, use deployment-specific query
    if (deploymentId !== null && deploymentId !== undefined) {
      console.log("getLatestMOBConfig: querying with deploymentId:", deploymentId);
      resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listMOBConfigsByAppInstanceDeploymentId, {
          appInstance: appInstance,
          mobDeploymentId: {
            eq: deploymentId
          }
          // No limit - fetch all configs for this deployment
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      console.log("getLatestMOBConfig: deployment query result:", resultSet.data.listMOBConfigsByAppInstanceDeploymentId.items);

      // Find config with highest mobVersion for this deployment
      if (resultSet.data.listMOBConfigsByAppInstanceDeploymentId.items.length > 0) {
        let latestConfig = null;
        for (let i = 0; i < resultSet.data.listMOBConfigsByAppInstanceDeploymentId.items.length; i++) {
          let config = resultSet.data.listMOBConfigsByAppInstanceDeploymentId.items[i];
          if (latestConfig === null || config.mobVersion > latestConfig.mobVersion) {
            latestConfig = config;
          }
        }
        console.log("getLatestMOBConfig: selected config with mobVersion:", latestConfig.mobVersion);
        return latestConfig;
      } else {
        console.log("getLatestMOBConfig: no config found for deploymentId:", deploymentId, "- falling back to default");
      }
    }

    // Default behavior: query by appInstance and mobVersion (backward compatibility)
    console.log("getLatestMOBConfig: using default query (no deployment filter)");
    resultSet = await APIAdapter.graphQLCall(
      graphqlOperation(listMOBConfigsByAppInstanceMobVersion, {
        appInstance: appInstance,
        sortDirection: "DESC",
        limit: 1
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );

    let mobConfigLatest = null;
    for (
      let i = 0;
      i < resultSet.data.listMOBConfigsByAppInstanceMobVersion.items.length;
      i++
    ) {
      let mobc = resultSet.data.listMOBConfigsByAppInstanceMobVersion.items[i];
      if (mobConfigLatest == null) {
        mobConfigLatest = mobc;
      }

      if (mobConfigLatest.mobVersion < mobc.mobVersion) {
        mobConfigLatest = mobc;
      }
    }

    console.log("getLatestMOBConfig: default query returned mobVersion:", mobConfigLatest?.mobVersion);
    return mobConfigLatest;
  }

  /**
   * API_KEY
   *
   * @param {*} appInstance
   * @param {*} mobVersion
   * @returns
   */
  static async listLocations(appInstance, mobVersion) {
    try {
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listLocationsByAppInstance, {
          appInstance: appInstance,
          mobVersion: {
            eq: mobVersion,
          },
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      return resultSet.data.listLocationsByAppInstance.items;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * API_KEY
   *
   * @param {*} appInstance
   * @param {*} mobVersion
   * @returns
   */
  static async listSkillLevelClients(appInstance, mobVersion) {
    try {
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listSkillLevelClientsByAppInstance, {
          appInstance: appInstance,
          mobVersion: {
            eq: mobVersion,
          },
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      return resultSet.data.listSkillLevelClientsByAppInstance.items;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * API_KEY
   *
   * @param {*} appInstance
   * @param {*} mobVersion
   * @returns
   */
  static async listPointSell(appInstance, mobVersion) {
    try {
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listPointSellsByAppInstance, {
          appInstance: appInstance,
          mobVersion: {
            eq: mobVersion,
          },
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      return resultSet.data.listPointSellsByAppInstance.items;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * Reads every DEFAULT schedule of the snapshot in one scope, with no sort condition.
   *
   * The sort key of a DEFAULT row is the unpadded point-sell id compared as text, so a range
   * condition on it silently answers with the wrong subset. Do not add one, and page with
   * `nextToken` rather than with a sort range. Reason and alternatives:
   * `__docs/features/rental/opening-hours/technical.md`, "Sort-key contract of the DEFAULT
   * scope" (MOB-096). Guarded by `src/test/APIAdapter.openHoursQueryShape.test.js`.
   *
   * A failed read REJECTS (MOB-094). An empty list means "no branch has a default schedule",
   * which leaves a branch unrestricted; a failed read must not look like that. The store
   * records the failure and blocks the booking (doctrine C34).
   */
  static async listDefaultOpenHours(appInstance, mobVersion) {
    try {
      const scope = appInstance + "#" + mobVersion + "#DEFAULT";
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listPointSellOpenHoursByScope, {
          scheduleScope: scope,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      const result = resultSet?.data?.listPointSellOpenHoursByScope;
      if (result == null || !Array.isArray(result.items)) {
        throw new Error("listDefaultOpenHours -- the response holds no result");
      }
      return result.items;
    } catch (error) {
      console.error("listDefaultOpenHours -- the read failed", error);
      throw error;
    }
  }

  /**
   * Reads the dated exception schedules of 1 branch that overlap a window.
   *
   * A failed read REJECTS (MOB-440), like `listDefaultOpenHours`. An empty `items` is a real
   * "no exception" and resolves `[]`; a failed read must not look like that, because the store
   * would cache it for the session and a holiday closure would be ignored (doctrine C34).
   */
  static async listExceptionOpenHours(
    appInstance,
    mobVersion,
    pointSellMssId,
    windowFrom,
    windowTo
  ) {
    try {
      const scope =
        appInstance + "#" + mobVersion + "#" + pointSellMssId + "#EXCEPTION";
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listPointSellOpenHoursByScope, {
          scheduleScope: scope,
          scheduleSort: { ge: windowFrom },
          filter: { dateFrom: { le: windowTo } },
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      const result = resultSet?.data?.listPointSellOpenHoursByScope;
      if (result == null || !Array.isArray(result.items)) {
        throw new Error("listExceptionOpenHours -- the response holds no result");
      }
      return result.items;
    } catch (error) {
      console.error("listExceptionOpenHours -- the read failed", error);
      throw error;
    }
  }

  static async listPointMeeting(appInstance, mobVersion) {
    try {
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listPointMeetingsByAppInstance, {
          appInstance: appInstance,
          mobVersion: {
            eq: mobVersion,
          },
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      return resultSet.data.listPointMeetingsByAppInstance.items;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * API_KEY
   *
   * @param {*} appInstance
   * @param {*} mobVersion
   * @returns
   */
  static async listStaticContent(appInstance, mobVersion) {
    try {
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listStaticContentByAppInstance, {
          appInstance: appInstance,
          mobVersion: {
            eq: mobVersion,
          },
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      return resultSet.data.listStaticContentByAppInstance.items;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  static async getStaticContent(staticContentId) {
    try {
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(getStaticContent, {
          id: staticContentId,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      return resultSet.data.getStaticContent;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   *
   * @param {*} appInstance
   * @param {*} mobVersion
   * @param {*} productType
   * @returns
   */
  static async loadListCategoriesAndProductsByProductType(
    appInstance,
    mobVersion,
    productType
  ) {
    try {
      // The school document also carries the Packets of a camp with their members, for the hint of
      // the add-on on the card of a run (MOB-517 ticket 05). It is 1 request, as before.
      const categoriesQuery =
        productType === "SCHOOL" ? listSchoolCategoriesByProductType : listRentCategoriesByProductType;
      let resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(categoriesQuery, {
          appInstance: appInstance,
          mobVersion: mobVersion,
          productType: productType,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      return resultSet.data.listCategoriesByProductType.items.sort(
        function compare(a, b) {
          return a.orderNumber - b.orderNumber;
        }
      );
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  static async getProduct(productId) {
    const result = await APIAdapter.graphQLCall(
      graphqlOperation(getProduct, {
        id: productId,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );

    let product = result.data.getProduct;
    return product;
  }

  /**
   * The companion product of a lesson product, or null (MOB-542, spec CP-003). The selection is
   * lightweight: no `category`, `deposit` or `pointMeetings`. The id of a product in the export
   * is `{appInstance}_{mobVersion}_{mssId}`, so the product is read by `mssId` within the
   * `mobVersion` of the order. No reference, or no product in the export, gives null; the
   * caller prices that as unpriceable.
   */
  static async getCompanionProduct(appInstance, mobVersion, companionProductMssId) {
    if (companionProductMssId == null) {
      return null;
    }
    const result = await APIAdapter.graphQLCall(
      graphqlOperation(getCompanionProduct, {
        id: appInstance + "_" + mobVersion + "_" + companionProductMssId,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );
    return result.data.getProduct;
  }

  static async getProductPacket(productPacketId) {
    const result = await APIAdapter.graphQLCall(
      graphqlOperation(getProductPacket, {
        id: productPacketId,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );

    let productPacket = result.data.getProductPacket;
    return productPacket;
  }

  /**
   * Lists the active+future Product Availability Intervals (PAI) cached for a product.
   * @param {string} productScope "{appInstance}#{mobVersion}#{productMssId}"
   * @returns array of { dateFrom, dateTo, availabilityPercent, ... }
   */
  static async listProductAvailabilityIntervalsByProduct(productScope) {
    const result = await APIAdapter.graphQLCall(
      graphqlOperation(listProductAvailabilityIntervalsByProduct, {
        productScope: productScope,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );
    return result.data.listProductAvailabilityIntervalsByProduct.items;
  }

  /**
   * Vytvori novou rozpracovanou objednavku ve stavu DRAFT
   *
   * @param {OrderrDraftIn} createOrderrDraftIn
   * @returns ID of created orderr
   */
  static async createOrderrDraft(createOrderrDraftIn) {
    try {
      const result = await APIAdapter.graphQLCall(
        graphqlOperation(createOrderrDraft, {
          input: createOrderrDraftIn,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      console.log("objednavka zalozena");
      console.log(result.data.createOrderrDraft);

      const draft = result.data != null ? result.data.createOrderrDraft : null;
      if (draft == null || draft.id == null) {
        throw new Error("createOrderrDraft: odpoved neobsahuje id objednavky");
      }

      return draft.id;
    } catch (error) {
      // MOB-040: this used to `return null`, which made a failed draft indistinguishable
      // from a created one. AppSync answers a rejected mutation with HTTP 200 and a
      // GraphQL-errors body, so the ordinary transport/backend failure landed here too and
      // the caller went on to read an order back with that null id. Failure has to reach
      // the caller; every caller of this method treats a rejection as "no order exists".
      console.error("Nepodarilo se zalozit objednavku", error);
      throw error;
    }
  }

  static async getOrderr(orderrId) {
    try {
      const result = await APIAdapter.graphQLCall(
        graphqlOperation(getOrderr, {
          id: orderrId,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      return result.data.getOrderr;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  static async getPayment(paymentId) {
    console.log("getPayment");
    try {
      const result = await APIAdapter.graphQLCall(
        graphqlOperation(getPayment, {
          id: paymentId,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      console.log("getPayment -- result");
      console.log(result.data.getPayment);
      return result.data.getPayment;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  static async removeOrderrItemGroupFromCart(orderrItemGroupId) {
    try {
      const result = await APIAdapter.graphQLCall(
        graphqlOperation(removeFromCart, {
          orderrItemGroupId: orderrItemGroupId,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      console.log(result);
    } catch (error) {
      console.error(error);
    }
  }

  static async addOrderrItemGroupToCart(addToCartIn) {
    console.log("addToCartIn - before transform:");
    console.log(addToCartIn);

    let ois = addToCartIn.orderrItems;
    let oisTransf = [];
    for (let i = 0; i < ois.length; i++) {
      let oi = {
        ...ois[i],
      };
      if (oi.languages !== undefined) {
        oi.languages = APIAdapter.toStoredLanguageCodes(oi.languages);
      }
      if (oi.language !== undefined && oi.language !== null) {
        oi.language = String(oi.language).trim().toUpperCase();
      }
      if (ois[i].article != null) {
        oi.article = {
          mssId: ois[i].article.mssId,
          articleType: ois[i].article.articleType,
          productId: ois[i].article.productId,
          pointSellId: ois[i].article.pointSellId,
          title: ois[i].article.title,
          description: ois[i].article.description,
          articleCountTotal: ois[i].article.articleCountTotal,
          // MOB-078 - `articleNumber` / `articleNumber2` are the shop's internal serials.
          // `rentArticleAvailability` no longer returns them and nothing reads them off the
          // order, so they are not copied onto the cart item either.
          size: ois[i].article.size,
          sizeUnit: ois[i].article.sizeUnit,
          rentLevel: ois[i].article.rentLevel,
          rentAgeGroup: ois[i].article.rentAgeGroup,
          rentGender: ois[i].article.rentGender,
          aggItem: ois[i].article.aggItem,
        };
      }
      oisTransf.push(oi);
    }

    addToCartIn.orderrItems = oisTransf;

    console.log("addToCartIn - after transform:");
    console.log(addToCartIn);

    const result = await APIAdapter.graphQLCall(
      graphqlOperation(addToCart, {
        input: addToCartIn,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );
    console.log(result);
  }

  static async updateOrderrPickupAndPaymentInfo(
    orderrId,
    datePickUp,
    paymentMethod,
    dateTryOut,
    description
  ) {
    try {
      const result = await APIAdapter.graphQLCall(
        graphqlOperation(updateOrderrPickupAndPaymentInfo, {
          orderrId: orderrId,
          datePickUp: datePickUp,
          paymentMethod: paymentMethod,
          dateTryOut: dateTryOut,
          description: description
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      console.log("updateOrderrPickupAndPaymentInfo -- done:");
      console.log(result.data.updateOrderrPickupAndPaymentInfo);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async checkoutOrderr(orderrId, createSubjectIn) {
    try {
      let updateResult = await APIAdapter.graphQLCall(
        graphqlOperation(checkoutOrderr, {
          orderrId: orderrId,
          subjectIn: createSubjectIn,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      console.log(updateResult);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * V1 implementation - calls schoolListVariants GraphQL query
   */
  static async schoolListVariants(
    appInstance,
    mobVersion,
    dateFrom,
    dateTo,
    locationMssId,
    pointMeetingMssId,
    skillMssId,
    languageCodes,
    requestedLengthTotal,
    requestedLengthAvg,
    candidateSlots
  ) {
    console.log("schoolListVariants");

    const resultset = await APIAdapter.graphQLCall(
      graphqlOperation(schoolListVariants, {
        appInstance: appInstance,
        mobVersion: mobVersion,
        locationMssId: locationMssId,
        pointMeetingMssId: pointMeetingMssId,
        skillMssId: skillMssId,
        languageCodes: APIAdapter.toFilterLanguageCodes(languageCodes),
        dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
        dateTo: moment(dateTo).format("YYYY-MM-DD"),
        requestedLengthTotal: requestedLengthTotal,
        requestedLengthAvg: requestedLengthAvg,
        candidateSlots: candidateSlots,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );
    return resultset.data.schoolListVariants;
  }

  /**
   * V1 implementation - calls schoolListFreeSlots GraphQL query
   */
  static async schoolListFreeSlots(
    appInstance,
    mobVersion,
    dateFrom,
    dateTo,
    variantIds,
    candidateSlots,
    pointMeetingMssId
  ) {
    console.log("schoolListFreeSlots");

    console.log("ID pointu: ");
    console.log(pointMeetingMssId);

    const resultset = await APIAdapter.graphQLCall(
      graphqlOperation(schoolListFreeSlots, {
        appInstance: appInstance,
        mobVersion: mobVersion,
        dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
        dateTo: moment(dateTo).format("YYYY-MM-DD"),
        variantIds: variantIds,
        candidateSlots: candidateSlots,
        pointMeetingMssId: pointMeetingMssId,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );
    return resultset.data.schoolListFreeSlots;
  }

  static async schoolListVariantsV2(
    appInstance,
    mobVersion,
    dateFrom,
    dateTo,
    locationMssId,
    pointMeetingMssId,
    skillMssId,
    skillLevelClientMssId,
    ageGroupMssIds,
    languageCodes,
    requestedLengthTotal,
    requestedLengthAvg,
    candidateSlots
  ) {
    console.log("schoolListVariantsV2");

    const resultset = await APIAdapter.graphQLCall(
      graphqlOperation(schoolListVariantsV2, {
        appInstance: appInstance,
        mobVersion: mobVersion,
        locationMssId: locationMssId,
        pointMeetingMssId: pointMeetingMssId,
        skillMssId: skillMssId,
        skillLevelClientMssId: skillLevelClientMssId,
        ageGroupMssIds: ageGroupMssIds,
        languageCodes: APIAdapter.toFilterLanguageCodes(languageCodes),
        dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
        dateTo: moment(dateTo).format("YYYY-MM-DD"),
        requestedLengthTotal: requestedLengthTotal,
        requestedLengthAvg: requestedLengthAvg,
        candidateSlots: candidateSlots,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );
    return resultset.data.schoolListVariantsV2;
  }

  static async schoolListFreeSlotsV2(
    appInstance,
    mobVersion,
    dateFrom,
    dateTo,
    variantIds,
    candidateSlots,
    pointMeetingMssId
  ) {
    console.log("schoolListFreeSlotsV2");

    console.log("ID pointu: ");
    console.log(pointMeetingMssId);

    const resultset = await APIAdapter.graphQLCall(
      graphqlOperation(schoolListFreeSlotsV2, {
        appInstance: appInstance,
        mobVersion: mobVersion,
        dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
        dateTo: moment(dateTo).format("YYYY-MM-DD"),
        variantIds: variantIds,
        candidateSlots: candidateSlots,
        pointMeetingMssId: pointMeetingMssId,
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );
    return resultset.data.schoolListFreeSlotsV2;
  }

  static async loadRentSizeAvailability(
    appInstance,
    mobVersion,
    pointSellId,
    productId,
    dateFrom,
    dateTo
  ) {
    console.log("loadRentSizeAvailability");

    const resultset = await APIAdapter.graphQLCall(
      graphqlOperation(rentSizeAvailability, {
        appInstance: appInstance,
        mobVersion: mobVersion,
        pointSellId: pointSellId,
        productId: productId,
        dateFrom: toWireMinute(dateFrom),
        dateTo: toWireMinute(dateTo),
      }),
      APIAdapter.AUTH_MODE_API_KEY
    );

    return resultset.data.rentSizeAvailability;
  }

  // The slot grid of `RentTimeslotCalendar` calls this name. It read its fake-UTC slot with
  // `moment.utc`. Since MOB-058 both entry points send the digits in the one form, so this one
  // delegates.
  static rentSizeAvailabilityBySlot(
    appInstance,
    mobVersion,
    pointSellId,
    productId,
    dateFrom,
    dateTo
  ) {
    return APIAdapter.loadRentSizeAvailability(
      appInstance,
      mobVersion,
      pointSellId,
      productId,
      dateFrom,
      dateTo
    );
  }

  static async loadRentArticlesBySize(
    appInstance,
    mobVersion,
    pointSellId,
    productId,
    dateStart,
    dateEnd,
    selectedSize
  ) {
    try {
      const resultset = await APIAdapter.graphQLCall(
        graphqlOperation(rentArticleAvailability, {
          appInstance: appInstance,
          mobVersion: mobVersion,
          pointSellId: pointSellId,
          productId: productId,
          dateFrom: toWireMinute(dateStart),
          dateTo: toWireMinute(dateEnd),
          size: selectedSize != null ? selectedSize.size : null,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      console.log(resultset);
      return resultset.data.rentArticleAvailability;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async listRentProductRequirements(appInstance, mobVersion) {
    try {
      const resultSet = await APIAdapter.graphQLCall(
        graphqlOperation(listRentProductRequirementByAppInstance, {
          appInstance: appInstance,
          mobVersion: {
            eq: mobVersion,
          },
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );

      return resultSet.data.listRentProductRequirementByAppInstance.items;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  static async schoolListGroupCourse(
    appInstance,
    mobVersion,
    productMssId,
    locationMssId,
    skillLevelClientMssId,
    languageCodes,
    dateFrom,
    dateTo
  ) {
    try {
      const resultset = await APIAdapter.graphQLCall(
        graphqlOperation(schoolListGroupCourse, {
          appInstance,
          mobVersion,
          productMssId,
          locationMssId,
          skillLevelClientMssId,
          languageCodes: APIAdapter.toFilterLanguageCodes(languageCodes),
          dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
          dateTo: moment(dateTo).format("YYYY-MM-DD"),
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      console.log(resultset);
      return resultset.data.schoolListGroupCourse;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getGroupCourseByMssId(
    appInstance,
    mobVersion,
    productMssId,
    groupCourseMssId
  ) {
    try {
      const resultset = await APIAdapter.graphQLCall(
        graphqlOperation(getGroupCourseByMssId, {
          appInstance,
          mobVersion,
          productMssId,
          groupCourseMssId
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      console.log(resultset);
      return resultset.data.getGroupCourseByMssId;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async schoolListGroupCourseLesson(
      appInstance,
      mobVersion,
      groupCourseMssId,
      dateFrom,
      dateTo
  ) {
    try {
      const resultset = await APIAdapter.graphQLCall(
        graphqlOperation(schoolListGroupCourseLesson, {
          appInstance,
          mobVersion,
          groupCourseMssId,
          dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
          dateTo: moment(dateTo).format("YYYY-MM-DD")
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      console.log(resultset);
      return resultset.data.schoolListGroupCourseLesson;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async applyDiscountCode(orderId, code) {
    console.log("API Adapter:: applyDiscountCode");
    console.log(orderId);
    console.log(code);
    try {
      const resultset = await APIAdapter.graphQLCall(
        graphqlOperation(applyDiscountCode, {
          orderrId: orderId,
          code: code,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      return resultset;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  static async removeDiscountCode(orderrId) {
    console.log("API Adapter:: removeDiscountCode");
    console.log(orderrId);
    try {
      const resultset = await APIAdapter.graphQLCall(
        graphqlOperation(removeDiscountCode, {
          orderrId: orderrId,
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      return resultset;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  static async sendCourseInquiry(mobConfig, locale, courseInquiryPayload) {
    console.log("API Adapter:: sendCourseInquiry");
    console.log(mobConfig);
    console.log(courseInquiryPayload);

    // appInstance: $appInstance
    // mobVersion: $mobVersion
    // locale: $locale

    try {
      const resultset = await APIAdapter.graphQLCall(
        graphqlOperation(sendCourseInquiry, {
          appInstance: mobConfig.appInstance,
          mobVersion: mobConfig.mobVersion,
          locale: locale,
          courseInquiryPayload: {
            ...courseInquiryPayload,
            languages: APIAdapter.toStoredLanguageCodes(courseInquiryPayload.languages),
            age: APIAdapter.toInquiryAge(courseInquiryPayload.age),
          },
        }),
        APIAdapter.AUTH_MODE_API_KEY
      );
      return resultset;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }
}

export default APIAdapter;
